import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { type SupportedLanguage } from "@/i18n";

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  image: string | null;
  published_at: string;
  is_published: boolean;
  // Translation fields
  title_en?: string | null;
  title_es?: string | null;
  excerpt_en?: string | null;
  excerpt_es?: string | null;
  content_en?: string | null;
  content_es?: string | null;
}

export interface LocalizedBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  image: string | null;
  published_at: string;
  is_published: boolean;
}

export const categories = ["Tous", "Conseils", "Destinations", "Itinéraires", "Actualités"];

// Helper to get localized content
const getLocalizedPost = (post: BlogPost, lang: string): LocalizedBlogPost => {
  if (lang === "en" && post.title_en) {
    return {
      ...post,
      title: post.title_en,
      excerpt: post.excerpt_en || post.excerpt,
      content: post.content_en || post.content,
    };
  }
  if (lang === "es" && post.title_es) {
    return {
      ...post,
      title: post.title_es,
      excerpt: post.excerpt_es || post.excerpt,
      content: post.content_es || post.content,
    };
  }
  // Default to French
  return post;
};

export const useBlogPosts = (category?: string) => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLanguage = (lang as SupportedLanguage) || (i18n.language as SupportedLanguage) || 'fr';
  
  return useQuery({
    queryKey: ["blog-posts", category, currentLanguage],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      
      if (category && category !== "Tous") {
        query = query.eq("category", category);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Map to localized versions
      return (data as BlogPost[]).map(post => getLocalizedPost(post, currentLanguage));
    },
  });
};

export const useBlogPost = (slug: string | undefined) => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLanguage = (lang as SupportedLanguage) || (i18n.language as SupportedLanguage) || 'fr';
  
  return useQuery({
    queryKey: ["blog-post", slug, currentLanguage],
    queryFn: async () => {
      if (!slug) return null;
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .eq("slug", slug)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) return null;
      
      return getLocalizedPost(data as BlogPost, currentLanguage);
    },
    enabled: !!slug,
  });
};

export const useRelatedPosts = (category: string | undefined, excludeId: string | undefined) => {
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLanguage = (lang as SupportedLanguage) || (i18n.language as SupportedLanguage) || 'fr';
  
  return useQuery({
    queryKey: ["related-posts", category, excludeId, currentLanguage],
    queryFn: async () => {
      if (!category || !excludeId) return [];
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .eq("category", category)
        .neq("id", excludeId)
        .order("published_at", { ascending: false })
        .limit(3);
      
      if (error) throw error;
      
      return (data as BlogPost[]).map(post => getLocalizedPost(post, currentLanguage));
    },
    enabled: !!category && !!excludeId,
  });
};

// Helper to format date
export const formatBlogDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// Helper to estimate read time
export const estimateReadTime = (content: string): string => {
  const wordsPerMinute = 200;
  const textContent = content.replace(/<[^>]+>/g, "");
  const words = textContent.split(/\s+/).length;
  const minutes = Math.ceil(words / wordsPerMinute);
  return `${minutes} min`;
};
